"use client"

import { useState, useEffect, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js"
import {
  MessageCircle,
  Globe,
  Users,
  Check,
  Phone,
  Mail,
  MapPin,
  Building,
  ShoppingBag,
  FileText,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
} from "lucide-react"
import "./App.css"

function App() {
  const [paypalClientId, setPaypalClientId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [exchangeRate, setExchangeRate] = useState<number | null>(null)
  const [currentVideo, setCurrentVideo] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  const videos = [
    {
      id: 0,
      src: "https://customer-zur2k0r44n3vzlo3.cloudflarestream.com/a20cec2bb81b348f5a6bdc3fe42505bf/iframe",
      title: "",
    },
    {
      id: 1,
      src: "https://customer-zur2k0r44n3vzlo3.cloudflarestream.com/76a3aa4034e89a04020230350b485bf3/iframe",
      title: "AI-Enhanced WhatsApp",
    },
    {
      id: 1,
      src: "https://customer-zur2k0r44n3vzlo3.cloudflarestream.com/fd18602b97f19093f506a23737bdbacd/iframe",
      title: "AI-Optimized Whatsapp Bulk Messaging",
    },
  ]

  const nextVideo = useCallback(() => {
    setCurrentVideo((prev) => (prev + 1) % videos.length)
  }, [videos.length])

  const prevVideo = useCallback(() => {
    setCurrentVideo((prev) => (prev - 1 + videos.length) % videos.length)
  }, [videos.length])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextVideo()
      }, 10000) // Change video every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isAutoPlaying, nextVideo])

  useEffect(() => {
    Promise.all([
      fetch("https://minimenuca.pthamiz.workers.dev/api/paypal-client-id"),
      fetch("https://minimenuca.pthamiz.workers.dev/api/exchange-rate"),
    ])
      .then(([clientIdResponse, exchangeRateResponse]) =>
        Promise.all([clientIdResponse.json(), exchangeRateResponse.json()]),
      )
      .then(([clientIdData, exchangeRateData]) => {
        if (!clientIdData.clientId) {
          throw new Error("Invalid PayPal Client ID received")
        }
        console.log("PayPal Client ID received:", clientIdData.clientId)
        setPaypalClientId(clientIdData.clientId)
        setExchangeRate(exchangeRateData.rate)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error("Error fetching data:", error)
        setError("Failed to load PayPal. Please try again later.")
        setIsLoading(false)
      })
  }, [])

  const amountINR = 100000.0
  const estimatedAmountCAD = exchangeRate ? (amountINR * exchangeRate).toFixed(2) : null

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6">
      <motion.div
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl text-center max-w-4xl w-full"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <img
          src="/NewMMLogo_Transparent_NoBuffer.png"
          alt="MiniMenu AI System Logo"
          className="logo mx-auto mb-6 rounded-full bg-white/20 p-4"
        />
        <h1 className="text-3xl font-extrabold mb-4 bg-gradient-to-r from-yellow-400 to-pink-500 bg-clip-text text-transparent">
          Welcome to MiniMenu.ca
        </h1>
        <p className="text-lg font-light mb-6">
          Revolutionizing Digital Growth with AI-Powered Marketing. Your AI-Driven Partner for Smarter, Scalable, and
          Automated Digital Marketing Solutions.
        </p>

        {/* About Us Section */}
        <section className="mb-8 text-left border-b border-white/20 pb-8">
          <h2 className="text-2xl font-semibold mb-4">About MiniMenu.ca</h2>
          <p className="mb-4">
            MiniMenu.ca is a Calgary-based technology business specializing in AI-powered digital marketing solutions.
            As a licensed retail dealer operating via mail order, we focus exclusively on providing cutting-edge AI
            services including chatbots, WhatsApp marketing, and social media optimization.
          </p>
          <p className="mb-4">
            Founded by experienced professionals in AI and digital marketing, we're committed to helping businesses grow
            through intelligent automation and data-driven strategies. Our team combines expertise in artificial
            intelligence, machine learning, and digital marketing to create innovative online solutions that drive
            measurable results.
          </p>
          <p className="mb-4">
            While our business is registered in Canada, we're proud to serve clients globally, with a primary focus on
            the Indian market. Our AI-powered services are designed to revolutionize how businesses interact with their
            customers and manage their online presence, all delivered through our secure, cloud-based platforms.
          </p>
          <p>
            As we expand, we look forward to bringing our innovative AI solutions to businesses across Canada and
            beyond.
          </p>
        </section>

        {/* Updated Business Definition Video Section with Auto-Sliding */}
        <section className="mb-8 border-b border-white/20 pb-8">
          <h2 className="text-2xl font-semibold mb-4">Our Business Definition</h2>
          <p className="mb-4 text-left">
            Watch our comprehensive business overview videos to learn about our company, services, and commitment to
            innovation in AI-powered marketing solutions:
          </p>
          <div className="relative rounded-lg overflow-hidden bg-gray-900 shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentVideo}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative"
                style={{ paddingTop: "56.25%" }}
              >
                {videos[currentVideo].src.includes("manifest/video.m3u8") ? (
                  <video
                    src={videos[currentVideo].src}
                    style={{
                      border: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    controls
                    autoPlay
                    muted
                  />
                ) : (
                  <iframe
                    src={videos[currentVideo].src}
                    title="Watch Video"
                    style={{
                      border: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen={true}
                  ></iframe>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
              <h3 className="text-lg font-semibold text-white">{videos[currentVideo].title}</h3>
            </div>
            <button
              onClick={prevVideo}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 transition-colors duration-300 rounded-full p-2"
              aria-label="Previous video"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextVideo}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 transition-colors duration-300 rounded-full p-2"
              aria-label="Next video"
            >
              <ChevronRight size={24} />
            </button>
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="absolute bottom-4 right-4 bg-white/20 hover:bg-white/40 transition-colors duration-300 rounded-full p-2"
              aria-label={isAutoPlaying ? "Pause auto-play" : "Start auto-play"}
            >
              {isAutoPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
          </div>
          <div className="mt-4 flex justify-center items-center space-x-2">
            {videos.map((video, index) => (
              <button
                key={video.id}
                onClick={() => setCurrentVideo(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentVideo === index ? "bg-white scale-125" : "bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Switch to video ${index + 1}`}
              />
            ))}
          </div>
          <p className="mt-4 text-sm text-green-300 font-medium">
            All our AI-powered services, including AI-Enhanced WhatsApp Marketing and AI-Optimized Social Media
            Marketing, are now active and available to our clients.
          </p>
        </section>

        {/* Services Section */}
        <section className="mb-8 border-b border-white/20 pb-8">
          <h2 className="text-2xl font-semibold mb-4 text-left">Our Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
            <motion.div
              className="bg-white/10 p-6 rounded-lg text-center shadow-md"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Users size={40} className="mx-auto text-blue-300 mb-3" />
              <h3 className="text-lg font-semibold">AI-Driven Chatbots</h3>
              <p className="text-sm opacity-80 mt-2">
                Implement intelligent NLP-powered chatbots that automate customer interactions and provide instant
                support.
              </p>
              <span className="inline-block mt-3 px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                Active Service
              </span>
            </motion.div>

            <motion.div
              className="bg-white/10 p-6 rounded-lg text-center shadow-md"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <MessageCircle size={40} className="mx-auto text-green-300 mb-3" />
              <h3 className="text-lg font-semibold">AI-Enhanced WhatsApp Marketing</h3>
              <p className="text-sm opacity-80 mt-2">
                Advanced WhatsApp marketing solutions powered by AI for better customer engagement.
              </p>
              <span className="inline-block mt-3 px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                Active Service
              </span>
            </motion.div>

            <motion.div
              className="bg-white/10 p-6 rounded-lg text-center shadow-md"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Globe size={40} className="mx-auto text-green-300 mb-3" />
              <h3 className="text-lg font-semibold">AI-Optimized Social Media Marketing</h3>
              <p className="text-sm opacity-80 mt-2">
                Data-driven social media strategies enhanced by artificial intelligence.
              </p>
              <span className="inline-block mt-3 px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                Active Service
              </span>
            </motion.div>
          </div>
        </section>

        {/* Special Offer Section */}
        <motion.div
          className="mt-12 bg-gradient-to-r from-yellow-400 to-orange-500 p-4 sm:p-6 rounded-xl shadow-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4">Special Monthly Subscription Offer!</h2>
          <p className="text-lg mb-4">
            Subscribe to our AI-Powered Marketing Suite and get access to our current and upcoming services!
          </p>
          <div className="text-left mb-6 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <Check className="mr-2 text-green-800 flex-shrink-0" size={20} />
                <span className="text-sm sm:text-base">AI-Driven Chatbots</span>
              </div>
              <div className="ml-7 sm:ml-0 mt-1 sm:mt-0">
                <span className="line-through text-gray-500 mr-2 text-sm">₹30,000/month</span>
                <span className="font-bold text-green-800 text-sm">Included</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <Check className="mr-2 text-green-800 flex-shrink-0" size={20} />
                <span className="text-sm sm:text-base">AI-Enhanced WhatsApp Marketing</span>
              </div>
              <div className="ml-7 sm:ml-0 mt-1 sm:mt-0">
                <span className="line-through text-gray-500 mr-2 text-sm">₹30,000/month</span>
                <span className="font-bold text-green-800 text-sm">Included</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <Check className="mr-2 text-green-800 flex-shrink-0" size={20} />
                <span className="text-sm sm:text-base">AI-Optimized Social Media Marketing</span>
              </div>
              <div className="ml-7 sm:ml-0 mt-1 sm:mt-0">
                <span className="line-through text-gray-500 mr-2 text-sm">₹20,000/month</span>
                <span className="font-bold text-green-800 text-sm">Included</span>
              </div>
            </div>
          </div>
          <div className="text-right mt-4">
            <p className="text-lg font-bold">Total: ₹100000/month (Approximately $1485 CAD/month)</p>
            <p className="text-sm text-gray-700">
              You'll save up to ₹67,500 (Approximately $1,080 CAD) every month with our comprehensive package!
            </p>
          </div>
          <p className="mt-4 text-xs sm:text-sm text-gray-700">
            * This is a monthly subscription. You'll be billed ₹100000 (Approximately $1485 CAD) every month for access
            to all our current services. Additional services will be included at no extra cost as they become available.
            Currency conversion rates may vary.
          </p>
        </motion.div>

        <div className="flex flex-col gap-4 mt-6">
          <div className="mt-4">
            {isLoading ? (
              <p>Loading PayPal...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : paypalClientId ? (
              <>
                <p className="mb-4 text-lg font-semibold">
                  Monthly Subscription: ₹{amountINR.toFixed(2)} INR
                  {estimatedAmountCAD && ` (Estimated: $${estimatedAmountCAD} CAD)`}
                </p>
                <PayPalScriptProvider
                  options={{
                    clientId: paypalClientId,
                    currency: "CAD",
                    components: "buttons,funding-eligibility",
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical" }}
                    fundingSource="paypal"
                    createOrder={async () => {
                      try {
                        setOrderError(null)
                        const response = await fetch("https://minimenuca.pthamiz.workers.dev/api/paypal/create-order", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            amount: amountINR.toString(),
                            currency: "INR",
                          }),
                        })
                        console.log("Create Order Response:", response)
                        const order = await response.json()
                        console.log("Create Order Data:", order)
                        if (!order.id) {
                          console.error("Order ID is missing:", order)
                          throw new Error(
                            `Failed to create order. Order ID is missing. Status: ${response.status}, Body: ${JSON.stringify(order)}`,
                          )
                        }
                        return order.id
                      } catch (error) {
                        if (error instanceof Error) {
                          console.error("Failed to create order:", error)
                          setOrderError(`Failed to create order. Please try again. Details: ${error.message}`)
                        } else {
                          console.error("Failed to create order:", error)
                          setOrderError("Failed to create order. Please try again.")
                        }
                        throw error
                      }
                    }}
                    onApprove={async (data) => {
                      try {
                        const response = await fetch(
                          `https://minimenuca.pthamiz.workers.dev/api/paypal/capture-payment`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                              orderID: data.orderID,
                            }),
                          },
                        )
                        const details = await response.json()
                        if (response.ok) {
                          const payerName =
                            details.payer && details.payer.name ? details.payer.name.given_name : "the customer"
                          alert(`Transaction completed by ${payerName}`)
                        } else {
                          throw new Error("Payment capture failed")
                        }
                      } catch (error) {
                        console.error("Failed to capture payment:", error)
                        alert("Payment failed. Please try again.")
                      }
                    }}
                    onError={(err) => {
                      console.error("PayPal Error:", err)
                      setOrderError("An error occurred with the PayPal integration. Please try again.")
                    }}
                  />
                  {orderError && <p className="mt-2 text-sm text-red-500">{orderError}</p>}
                </PayPalScriptProvider>
                <p className="mt-2 text-sm text-gray-300">
                  Note: This is a monthly subscription of ₹130000 INR per month. You can cancel your subscription at any
                  time.
                </p>
              </>
            ) : (
              <p>Failed to load PayPal. Please try again later.</p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <section className="mt-8 text-left border-t border-white/20 pt-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">Canada</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Phone className="mr-2" size={20} />
                  <span>+1 825-962-2932 (Phone calls only)</span>
                </div>
                <div className="flex items-center">
                  <Mail className="mr-2" size={20} />
                  <a href="mailto:info@minimenu.ca" className="underline hover:text-white">
                    info@minimenu.ca
                  </a>
                </div>
                <div className="flex items-center">
                  <Mail className="mr-2" size={20} />
                  <a href="mailto:pthamiz@gmail.com" className="underline hover:text-white">
                    pthamiz@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-2" size={20} />
                  <span>Calgary, Alberta, Canada</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">India</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Phone className="mr-2" size={20} />
                  <span>+91 7094518055</span>
                </div>
                <div className="flex items-center">
                  <Mail className="mr-2" size={20} />
                  <a href="mailto:dprajan@minimenu.ca" className="underline hover:text-white">
                    dprajan@minimenu.ca
                  </a>
                </div>
                <p className="mt-2">
                  PrajanSusi Dharmalingam, our Rising Star and India Operations Lead, manages our technical support,
                  development, and marketing initiatives in India. As an independent contractor, he plays a crucial role
                  in our growth and client relations.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white/10 rounded-lg">
            <p className="text-sm">
              For business inquiries and partnership opportunities, please contact our CEO directly at:{" "}
              <a href="mailto:Arasan@minimenu.ca" className="underline hover:text-white">
                Arasan@minimenu.ca
              </a>
            </p>
          </div>
        </section>

        {/* Support Information */}
        <div className="mt-8 text-sm text-gray-300 border-t border-white/20 pt-8">
          <p>
            Customer Support Hours: Monday - Friday, 9 AM - 5 PM IST (Indian Standard Time)
            <br />
            Emergency Support: Available 24/7 for enterprise clients
          </p>
        </div>
        {/* Business Information Section */}
        <section className="mt-8 text-left border-t border-white/20 pt-8">
          <h2 className="text-2xl font-semibold mb-4">Business Information</h2>
          <div className="grid gap-4">
            <div className="flex items-center">
              <Building className="mr-2" size={20} />
              <span>Registered Business Name: MINIMENU.CA</span>
            </div>
            <div className="flex items-center">
              <ShoppingBag className="mr-2" size={20} />
              <span>Business Type: RETAIL DEALER - PREMISES (MAIL ORDER)</span>
            </div>
            <div className="flex items-center">
              <FileText className="mr-2" size={20} />
              <span>Business License Number: BL287362</span>
            </div>
            <div className="flex items-center">
              <Users className="mr-2" size={20} />
              <span>Ownership Type: Sole Proprietor</span>
            </div>
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              <span>License Start Date: December 10, 2024</span>
            </div>
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              <span>Registered Office: #815 815 4 AV SW, Calgary, Alberta, Canada T2P3G8</span>
            </div>
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              <span>Business License Mailing Address: #815 815 4 AV SW, Calgary, Alberta, Canada T2P3G8</span>
            </div>
          </div>
        </section>
      </motion.div>
    </div>
  )
}

export default App

